import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core';

import { Store } from '@ngrx/store';
import { combineLatest, map, Observable, of, switchMap, take } from 'rxjs';

import { TripType } from '@fcom/core/constants';
import { finShare } from '@fcom/rx';
import { NotificationTheme } from '@fcom/ui-components';
import { LanguageService } from '@fcom/ui-translate';
import { isPresent, isNotBlank } from '@fcom/utils';
import { BookingAppState } from '@fcom/booking';
import { Location } from '@fcom/core-api';

import { notificationWarnings } from '../../store';
import { Locations } from '../../interfaces/location-pair.interface';
import {
  NotificationData,
  NotificationSpecific,
  NotificationWarning,
  SeasonalNotificationData,
  WarningData,
} from '../../interfaces';

@Component({
  selector: 'fin-booking-widget-notifications',
  templateUrl: './booking-widget-notification.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BookingWidgetNotificationComponent implements OnInit {
  @Input()
  locations$: Observable<Locations> = of({});

  @Input()
  tripType$: Observable<TripType> = of(undefined);

  @Input()
  isAm = false;

  notifications$: Observable<NotificationData[]>;

  private readonly busConnectionLocationCodes = ['TKU', 'TMP', 'XTP', 'XTZ'];

  constructor(
    private store$: Store<BookingAppState>,
    private languageService: LanguageService
  ) {}

  ngOnInit(): void {
    this.notifications$ = combineLatest([
      this.locations$,
      this.tripType$,
      this.store$.pipe(notificationWarnings(), finShare()),
    ]).pipe(
      switchMap(([locations, tripType, warnings]) => {
        const destinationNotification$ = this.mapDestinationNotification(locations.destination);
        const storeNotifications = Object.entries(warnings).map((entry) =>
          this.mapWarningToNotification(entry, tripType, locations)
        );
        const busConnectionNotification = this.mapBusConnectionNotification(locations);
        const amEarnPointsNotification = this.isAm ? this.mapAmEarnPointsNotification() : undefined;

        return destinationNotification$.pipe(
          map((destinationNotification) =>
            [
              busConnectionNotification,
              destinationNotification,
              amEarnPointsNotification,
              ...storeNotifications,
            ].filter(Boolean)
          )
        );
      })
    );
  }

  private mapWarningToNotification(
    [type, warningData]: [string, WarningData],
    tripType: TripType,
    locations: Locations
  ): NotificationData {
    if (!warningData.isActive) {
      return undefined;
    }

    switch (type) {
      case NotificationWarning.SEASONAL_ROUTE:
        return isPresent(warningData?.data) ? this.mapSeasonalNotification(warningData.data, locations) : null;
      case NotificationWarning.NO_FLIGHTS:
        return this.mapNoFlightNotification(tripType, locations, false);
      case NotificationWarning.NO_AWARD_FLIGHTS:
        return this.mapNoFlightNotification(tripType, locations, true);
      case NotificationWarning.GENERAL:
        return this.mapFlightGeneralNotification();
      default:
        return undefined;
    }
  }

  private mapDestinationNotification(destination: Location): Observable<NotificationData> {
    if (!isPresent(destination?.countryCode)) {
      return of(undefined);
    }

    return this.languageService.translate(`dynamicNotifications.destination.${destination.countryCode}`).pipe(
      take(1),
      map((notificationText: string) =>
        isNotBlank(notificationText)
          ? {
              type: NotificationSpecific.DESTINATION_SPECIFIC,
              theme: NotificationTheme.INFO,
              text: { label: `dynamicNotifications.destination.${destination.countryCode}` },
            }
          : undefined
      ),
      finShare()
    );
  }

  private mapSeasonalNotification(
    seasonalDataNotification: SeasonalNotificationData,
    { origin, destination }: Locations
  ): NotificationData {
    return {
      type: NotificationWarning.SEASONAL_ROUTE,
      theme: NotificationTheme.INFO,
      text: {
        label: `timetable.seasonalNotification.${
          Object.keys(seasonalDataNotification).length <= 2 ? 'singular' : 'plural'
        }`,
        params: {
          ...seasonalDataNotification,
          origin: origin.cityName,
          destination: destination.cityName,
        },
      },
      link: {
        url: `/${this.languageService.langValue}/timetables?dest=${destination.locationCode}&origin=${origin.locationCode}`,
        text: 'routeNotification.timetable.forRouteLinkText',
      },
    };
  }

  private mapFlightGeneralNotification(): NotificationData {
    return {
      type: NotificationWarning.GENERAL,
      theme: NotificationTheme.DISRUPTION_LEVEL_THREE,
      text: {
        label: 'errors.booking.title',
      },
      text1: {
        label: 'errors.booking.description',
      },
    };
  }

  private mapNoFlightNotification(
    tripType: TripType,
    { origin, destination }: Locations,
    isAward: boolean
  ): NotificationData {
    return {
      type: isAward ? NotificationWarning.NO_AWARD_FLIGHTS : NotificationWarning.NO_FLIGHTS,
      theme: NotificationTheme.DISRUPTION_LEVEL_THREE,
      text: {
        label: isAward ? 'bookingSearch.errors.noAwardFlightsFound' : 'bookingSearch.errors.noFlightsFound',
      },
      ...(tripType !== TripType.MULTICITY &&
        !isAward && {
          text1: { label: 'routeNotification.timetable.checkTimeTableText' },
        }),
      link: {
        openInNewTab: true,
        ...(tripType === TripType.MULTICITY && { external: true }),
        url:
          tripType !== TripType.MULTICITY
            ? `/${this.languageService.langValue}/timetables?dest=${destination.locationCode}&origin=${origin.locationCode}`
            : 'bookingSearch.errors.multiCity.seeMoreLink.url',
        text:
          tripType !== TripType.MULTICITY
            ? 'routeNotification.timetable.forRouteLinkText'
            : 'bookingSearch.errors.multiCity.seeMoreLinkText',
      },
    };
  }

  private mapBusConnectionNotification(locations: Locations): NotificationData {
    const isBusConnectionNeeded = ['origin', 'destination'].some((key) =>
      this.busConnectionLocationCodes.includes(locations[key]?.locationCode)
    );

    return isBusConnectionNeeded
      ? {
          type: NotificationSpecific.BUS_CONNECTION,
          theme: NotificationTheme.INFO,
          text: { label: 'routeNotification.busConnections.notificationText' },
          link: {
            openInNewTab: true,
            url: 'routeNotification.busConnections.link.url',
            text: 'routeNotification.busConnections.linkText',
          },
        }
      : undefined;
  }

  private mapAmEarnPointsNotification(): NotificationData {
    return {
      type: NotificationSpecific.AM_EARN_POINTS,
      theme: NotificationTheme.INFO,
      text: { label: 'aurinkomatkat.notification.teaser' },
      text1: { label: 'aurinkomatkat.notification.content' },
    };
  }
}
